export interface Photo {
  file: string;
  w: number;
  h: number;
}

const photo = (file: string, w: number, h: number): Photo => ({ file: `images/${file}`, w, h });

export const PHOTOS = {
  mountBin: photo('mount-bin.webp', 1280, 853),
  street: photo('build-street.webp', 1280, 853),
  indoor: photo('build-indoor.webp', 1280, 853),
  visionTrial: photo('vision/trial.webp', 1152, 864),
} as const;

/** Finished Iris Waste builds from the brochure and solacetech.group/brands/iris. */
export const BUILDS = [
  { sku: 'IRIS-US-BAT-4G-BIN', id: 'street', photo: photo('build-street.webp', 1280, 853) },
  { sku: 'IRIS-AIV-AC-ETH-CEI', id: 'indoor', photo: photo('build-indoor.webp', 1280, 853) },
  { sku: 'IRIS-AIV-SOL-RF-POL', id: 'pole', photo: photo('build-pole.webp', 1280, 853) },
] as const;

export const AXES = ['sensing', 'power', 'conn', 'mount'] as const;

export const WASTE_OPTIONS = ['sla', 'carbon', 'portfolio', 'resident'] as const;

export const VISION_SECTORS = [
  { id: 'retail', photo: photo('vision/retail.webp', 1152, 864) },
  { id: 'warehouse', photo: photo('vision/warehouse.webp', 1152, 864) },
  { id: 'campus', photo: photo('vision/campus.webp', 1152, 864) },
  { id: 'city', photo: photo('vision/city.webp', 1152, 864) },
] as const;

export const VISION_CUSTOM_EXAMPLES = [1, 2, 3, 4] as const;

export const WHY_HOME = [1, 2, 3, 4, 5, 6] as const;
export const WHY_WASTE = [1, 2, 3, 4, 5, 6, 7] as const;
export const WHY_VISION = [1, 2, 3, 4, 5, 6] as const;

export const WASTE_SITES = [
  'residential_compound',
  'hotel',
  'mall',
  'hospital',
  'office',
  'factory',
  'city',
  'other',
] as const;

export const VISION_SITES = [
  'residential_compound',
  'hotel',
  'mall',
  'hospital',
  'office',
  'factory',
  'city',
  'other',
] as const;

export type DeviceKind = 'laptop' | 'phone';

/** A frameless app screen; its alt text is keyed `shot.<line>.<name>.<kind>`. */
export interface Shot extends Photo {
  alt: string;
  kind: DeviceKind;
  file2x: string;
}

const SHOT_SIZE: Record<DeviceKind, [number, number]> = { laptop: [1440, 900], phone: [390, 844] };

const shot = (line: 'waste' | 'vision', name: string, kind: DeviceKind): Shot => {
  const [w, h] = SHOT_SIZE[kind];
  const base = `images/${line}/iris-${line}-${name}-${kind}-frameless`;
  return { alt: `shot.${line}.${name}.${kind}`, kind, file: `${base}.webp`, file2x: `${base}@2x.webp`, w, h };
};

/** Commercial status on the site. Never "coming soon" or "live". */
export type FeatureStatus = 'included' | 'on_request' | 'sample';

/** One software feature: copy keyed `<prefix>.<id>.*`, shown on a laptop and/or phones. */
export interface Feature {
  id: string;
  laptop?: Shot;
  phones: Shot[];
  status?: FeatureStatus;
}

const w = (name: string, kind: DeviceKind) => shot('waste', name, kind);
const v = (name: string, kind: DeviceKind) => shot('vision', name, kind);

export const WASTE_SOFTWARE: Feature[] = [
  { id: 'map', laptop: w('map', 'laptop'), phones: [w('operator-bin', 'phone')] },
  { id: 'alerts', laptop: w('operator-alerts', 'laptop'), phones: [] },
  { id: 'reports', laptop: w('executive-view', 'laptop'), phones: [w('executive-view', 'phone')] },
  { id: 'setup', laptop: w('tenant-admin-groups', 'laptop'), phones: [w('installer-activation-code', 'phone')] },
  { id: 'routes', status: 'on_request', laptop: w('routes', 'laptop'), phones: [] },
  { id: 'crew', status: 'on_request', phones: [w('crew', 'phone'), w('operator-bin', 'phone')] },
];

export const VISION_SOFTWARE: Feature[] = [
  { id: 'alerts', status: 'sample', laptop: v('alerts', 'laptop'), phones: [v('alert', 'phone')] },
  { id: 'cameras', status: 'sample', laptop: v('cameras', 'laptop'), phones: [] },
  { id: 'counts', status: 'sample', laptop: v('counts', 'laptop'), phones: [v('counts', 'phone')] },
  { id: 'zones', status: 'sample', laptop: v('zones', 'laptop'), phones: [] },
  { id: 'report', status: 'sample', laptop: v('report', 'laptop'), phones: [] },
];

export const HOME_SHOT = {
  waste: w('map', 'laptop'),
  vision: v('alerts', 'laptop'),
} as const;

export const HERO_SHOT = {
  waste: {
    laptop: w('map', 'laptop'),
    phone: w('operator-bin', 'phone'),
  },
  vision: {
    laptop: v('alerts', 'laptop'),
    phone: v('alert', 'phone'),
  },
} as const;

export function src(p: Photo): string {
  return `${import.meta.env.BASE_URL}${p.file}`;
}
